import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { PLATFORM } from "../env";
import { useAuthApi } from "../hooks/useAuthApi";
import type { ApiKey } from "../types";

interface CreatedApiKey extends ApiKey {
  key: string;
}

export default function ApiKeys() {
  const authApi = useAuthApi();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await authApi<ApiKey[]>(`${PLATFORM}/api-keys`);
      setKeys(data ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load API keys");
    } finally {
      setLoading(false);
    }
  }, [authApi]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const data = await authApi<CreatedApiKey>(`${PLATFORM}/api-keys`, {
        method: "POST",
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (data) {
        setCreatedKey(data.key);
        setNewName("");
        await load();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create API key");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: number) => {
    setError(null);
    try {
      await authApi(`${PLATFORM}/api-keys/${id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete API key");
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Generate API Key
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Platform API keys authenticate requests with{" "}
        <code>Authorization: Bearer &lt;key&gt;</code>. Full key values are shown only once.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Key Prefix</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Last Used</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <CircularProgress size={24} />
              </TableCell>
            </TableRow>
          )}
          {!loading && keys.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary">
                  No platform API keys yet.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {keys.map((k) => (
            <TableRow key={k.id}>
              <TableCell>{k.name}</TableCell>
              <TableCell sx={{ fontFamily: "monospace" }}>{k.key_prefix}…</TableCell>
              <TableCell>{new Date(k.created_at).toLocaleString()}</TableCell>
              <TableCell>{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "—"}</TableCell>
              <TableCell align="right">
                <Tooltip title="Delete key">
                  <IconButton size="small" color="error" onClick={() => handleDelete(k.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Platform API Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Key name"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            placeholder="e.g. Load tester, CI automation"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !newName.trim()}>
            {creating ? <CircularProgress size={18} /> : "Generate"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createdKey !== null}
        onClose={() => {
          setCreatedKey(null);
          setCreateOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>API Key Created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key now — it will not be shown again.
          </Alert>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Typography
              sx={{
                fontFamily: "monospace",
                bgcolor: "action.hover",
                p: 1.5,
                borderRadius: 1,
                flexGrow: 1,
                wordBreak: "break-all",
              }}
            >
              {createdKey}
            </Typography>
            <Tooltip title="Copy">
              <IconButton onClick={() => createdKey && copyToClipboard(createdKey)}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setCreatedKey(null);
              setCreateOpen(false);
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
